import { fromLonLat } from 'ol/proj';
import { Theme } from '../helpers/hooks';
import { Coordinate } from 'ol/coordinate';
import { LatLng } from '@/types/api/Edge';

export type GeometryLike = Object & {
  type: string;
  coordinates: LatLng[] | LatLng | Coordinate[] | Coordinate;
};

export type FeatureLike = {
  type: 'Feature';
  geometry: GeometryLike;
  properties: any;
};

export type EdgeLike = {
  positions: [number, number][];
};

export type PropertyExtractor<T extends EdgeLike> = (
  edge: T,
  arrowHead: boolean,
  theme: Theme,
) => {
  [key: string]: unknown;
};

export const edgeToGeoJson = <T extends EdgeLike>(
  edge: T,
  propertyExtractor: PropertyExtractor<T>,
  displayArrowhead: boolean = false,
  arrowheadProps: { frequency?: number } & Object = {},
  theme: Theme = 'light',
): FeatureLike[] => {
  if (!edge?.positions) {
    return [
      {
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: [
            [0, 0],
            [0, 0],
          ],
        },
        properties: propertyExtractor(edge, false, theme),
      },
    ];
  }
  const edgeEnd = edge.positions[edge.positions.length - 1];
  const properties = propertyExtractor(edge, false, theme);

  const edgeFeature: FeatureLike = {
    type: 'Feature',
    geometry: {
      type: 'LineString',
      coordinates: edge.positions.map(([lat, lon]) => fromLonLat([lon, lat])),
    },
    properties: { ...properties, head: 0, angle: 0 },
  };

  const features: FeatureLike[] = [edgeFeature];

  // Calculating arrowheads
  const { frequency = 0 } = arrowheadProps;
  if (displayArrowhead && edgeEnd.length > 1) {
    if (frequency < 1) {
      const arrowheadCount = Math.floor(1 / (1 - frequency));

      const pathLength = edge.positions.reduce((acc, [lat, lon], i, arr) => {
        if (i === 0) return acc;
        const [prevLat, prevLon] = arr[i - 1];
        return acc + Math.hypot(lat - prevLat, lon - prevLon);
      }, 0);
      const arrowheadDistance = pathLength / arrowheadCount;

      let distance = 0;
      let arrowheadIndex = 1;
      let totalDist = 0;
      for (let i = 1; i < edge.positions.length; i++) {
        const [lat, lon] = edge.positions[i];
        const [prevLat, prevLon] = edge.positions[i - 1];
        const segmentLength = Math.hypot(lat - prevLat, lon - prevLon);
        distance += segmentLength;
        totalDist += segmentLength;

        while (
          distance >= arrowheadDistance &&
          arrowheadIndex < arrowheadCount
        ) {
          const rem =
            segmentLength - (totalDist - arrowheadDistance * arrowheadIndex);
          const angle = Math.atan2(lat - prevLat, lon - prevLon);
          const arrowPos = [
            prevLat + Math.sin(angle) * rem,
            prevLon + Math.cos(angle) * rem,
          ];
          features.push({
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: fromLonLat([arrowPos[1], arrowPos[0]]),
            },
            properties: { ...properties, head: 1, angle: Math.PI / 2 - angle },
          });
          arrowheadIndex++;
          distance -= arrowheadDistance;
        }
      }
    }

    // Add last arrowhead
    const angle = Math.atan2(
      edgeEnd[0] - edge.positions[edge.positions.length - 2][0],
      edgeEnd[1] - edge.positions[edge.positions.length - 2][1],
    );
    features.push({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: fromLonLat([edgeEnd[1], edgeEnd[0]]),
      },
      properties: { ...properties, head: 1, angle: Math.PI / 2 - angle },
    });
  }

  return features;
};

export const midPoint = (a: [number, number], b: [number, number]) => [
  (a[0] + b[0]) / 2,
  (a[1] + b[1]) / 2,
];

export const closestPointOnLine = (
  point: Coordinate,
  line: [Coordinate, Coordinate],
): Coordinate => {
  const [p1, p2] = line;
  const x0 = point[0];
  const y0 = point[1];
  const x1 = p1[0];
  const y1 = p1[1];
  const x2 = p2[0];
  const y2 = p2[1];

  const dx = x2 - x1;
  const dy = y2 - y1;

  if (dx === 0 && dy === 0) {
    return [x1, y1]; // Line segment is a point
  }

  const t = ((x0 - x1) * dx + (y0 - y1) * dy) / (dx * dx + dy * dy);

  if (t < 0) {
    return [x1, y1]; // Closest to the start of the segment
  } else if (t > 1) {
    return [x2, y2]; // Closest to the end of the segment
  }

  return [x1 + t * dx, y1 + t * dy]; // Point on the line segment
};
